import React, { useEffect, useState, useRef } from "react";
import { auth, db, storage } from "../firebase";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import {
  collection,
  getDocs,
  query,
  where,
  addDoc, // if you use addDoc elsewhere
} from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import api from "../services/api";
import ReviewBeforeSaveModal from "./ReviewBeforeSaveModal";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  useMediaQuery,
  useTheme,
  IconButton,
  Fade,
  TextField,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import UpgradePrompt from "../components/UpgradePrompt";
// import NominalCodeDropdown from "./NominalCodeDropdown";
import { useTrial } from "../contexts/TrialContext";
import { CATEGORY_SELECT_OPTIONS } from "../constants/expenseCategories";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import * as pdfjsLib from "pdfjs-dist";
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

async function convertPdfToImage(pdfFile) {
  const loadingTask = pdfjsLib.getDocument(URL.createObjectURL(pdfFile));
  const pdf = await loadingTask.promise;
  const page = await pdf.getPage(1);

  const viewport = page.getViewport({ scale: 2 });
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  canvas.width = viewport.width;
  canvas.height = viewport.height;

  await page.render({ canvasContext: context, viewport }).promise;

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, "image/png");
  });
}

function ReceiptUploader() {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadTask, setUploadTask] = useState(null);
  const [manualEntryMode, setManualEntryMode] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [reviewData, setReviewData] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [pendingReceipt, setPendingReceipt] = useState(null);
  const [manualTotal, setManualTotal] = useState("");
  const [manualSubtotal, setManualSubtotal] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [manualVAT, setManualVAT] = useState("");
  const [needsRetry, setNeedsRetry] = useState(false);
  const { plan, setIsPro, isTrialExpired, setIsTrialExpired } = useTrial();

  const [dragOver, setDragOver] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [status, setStatus] = useState("");
  const [showProgress, setShowProgress] = useState(false);
  const [showSuccessIcon, setShowSuccessIcon] = useState(false);
  const [notes, setNotes] = useState("");

  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;
        const snap = await getDocs(
          query(collection(db, "projects"), where("uid", "==", user.uid)),
        );
        setProjects(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        console.error("Failed to load projects", error);
      }
    };
    fetchProjects();
  }, []);

  const isFutureDate = date ? new Date(date) > new Date() : false;

  const handleUpload = async () => {
    if (!file || !auth.currentUser || isFutureDate) return;

    setUploading(true);
    setProcessing(false);
    setUploadProgress(0);
    setShowProgress(true);
    setShowSuccessIcon(false);
    setStatus("Processing...");

    let timeoutId;

    try {
      const uid = auth.currentUser.uid;
      const fileId = uuidv4();
      const storageRef = ref(storage, `receipts/${uid}/${fileId}`);
      const task = uploadBytesResumable(storageRef, file);
      setUploadTask(task);

      task.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
          setStatus(`Processing... ${Math.round(progress)}%`);
        },
        (error) => {
          if (error.code === "storage/canceled") {
            setSnackbar({
              open: true,
              message: "⚠️ Upload cancelled.",
              severity: "warning",
            });
          } else {
            setSnackbar({
              open: true,
              message: "❌ Upload failed. Please try again.",
              severity: "error",
            });
          }
          setUploading(false);
          setStatus("");
          setUploadProgress(0);
          setShowProgress(false);
          setProcessing(false);
          setShowSuccessIcon(false);
        },
        async () => {
          setUploadProgress(100);
          setStatus("Finalizing...");
          setProcessing(true);

          try {
            const downloadURL = await getDownloadURL(task.snapshot.ref);
            let blobForOCR = file;

            if (file.type === "application/pdf") {
              blobForOCR = await convertPdfToImage(file);
            }

            const formData = new FormData();
            formData.append("file", blobForOCR);

            const res = await api.post(
              `/ocr/parse-receipt?imageUrl=${encodeURIComponent(downloadURL)}`,
              formData,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                  Authorization: `Bearer ${await auth.currentUser.getIdToken()}`,
                },
              },
            );

            if (res.data.success === false && res.data.warning) {
              setSnackbar({
                open: true,
                message: res.data.warning,
                severity: "warning",
              });
              setNeedsRetry(true);
              resetUploadState();
              return;
            }

            let invoiceTotal, vatAmount, subtotal;

            if (manualEntryMode) {
              invoiceTotal = parseFloat(manualTotal);
              subtotal = parseFloat(manualSubtotal);
              vatAmount = parseFloat(manualVAT);
            } else {
              invoiceTotal = res.data.invoiceTotal ?? null;
              vatAmount = res.data.vatAmount ?? null;
              subtotal = res.data.subtotal ?? null;
            }

            // ⛔️ Do not proceed if invoiceTotal is null
            if (invoiceTotal === null) {
              setSnackbar({
                open: true,
                message:
                  "⚠️ Could not detect total from receipt. Please try again or enter manually.",
                severity: "warning",
              });
              resetUploadState();
              return;
            }

            // ✅ Show Review Modal
            setReviewData({
              fileId,
              fileName: file.name,
              downloadURL,
              invoiceTotal,
              vatAmount,
              subtotal,
              category: selectedCategory || "",
              notes: notes || "",
            });

            setReviewDialogOpen(true);
            setUploading(false); // Pause rest until user confirms
            setProcessing(false);
            setShowProgress(false);
            setUploadProgress(0);
            setStatus("");
            setShowSuccessIcon(false);
          } catch (err) {
            console.error("Finalizing error:", err);
            setSnackbar({
              open: true,
              message: "❌ Upload failed during processing.",
              severity: "error",
            });
            resetUploadState();
          }

          setProcessing(false);
          setUploading(false);
        },
      );
    } catch (err) {
      console.error("Upload error:", err);
      setSnackbar({
        open: true,
        message: "❌ Upload failed. Please try again.",
        severity: "error",
      });
      resetUploadState();
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  };

  const resetUploadState = () => {
    setStatus("");
    setUploadProgress(0);
    setShowProgress(false);
    setShowSuccessIcon(false);
    setUploading(false);
    setProcessing(false);
  };

  const handleReviewSave = async (finalData) => {
    try {
      if (!auth.currentUser) return;

      // ✅ Validate required fields
      if (
        !finalData.fileName ||
        !finalData.downloadURL ||
        !finalData.invoiceTotal
      ) {
        setSnackbar({
          open: true,
          message: "❌ Missing required receipt data. Please try again.",
          severity: "error",
        });
        return;
      }

      await api.post(
        "/receipts/upload",
        {
          fileName: finalData.fileName,
          imageUrl: finalData.downloadURL,
          invoiceTotal: finalData.invoiceTotal,
          vatAmount: finalData.vatAmount ?? null,
          subtotal: finalData.subtotal ?? null,
          amount: finalData.invoiceTotal,
          receiptDate: date,
          receiptMonth: date
            ? new Date(date).getMonth()
            : new Date().getMonth(),
          receiptYear: date
            ? new Date(date).getFullYear()
            : new Date().getFullYear(),
          isManual: manualEntryMode,
          projectId: selectedProjectId || null,
          category: finalData.category || null,
          // status: "Uploaded",
          notes: finalData.notes || "",
        },
        {
          headers: {
            Authorization: `Bearer ${await auth.currentUser.getIdToken()}`,
          },
        },
      );

      setSnackbar({
        open: true,
        message: "✅ Receipt saved successfully!",
        severity: "success",
      });

      sessionStorage.setItem("newReceiptId", finalData.fileId);
      setFile(null);
      setPreviewUrl(null);
      setShowSuccessIcon(true);
      setStatus("Upload complete!");
      if (fileInputRef.current) fileInputRef.current.value = "";

      setTimeout(() => {
        setShowProgress(false);
        setUploadProgress(0);
        setStatus("");
        setShowSuccessIcon(false);
      }, 1500);
    } catch (err) {
      console.error("Save failed after review:", err);
      setSnackbar({
        open: true,
        message: "❌ Failed to save receipt after review.",
        severity: "error",
      });
    } finally {
      setReviewDialogOpen(false);
    }
  };

  const handleCancel = () => {
    if (uploadTask) uploadTask.cancel();
  };

  const processFile = (incomingFile) => {
    if (!incomingFile) return;
    setNeedsRetry(false);

    setFile(incomingFile);
    const lower = incomingFile.name.toLowerCase();
    const isHEIC = lower.endsWith(".heic");
    const isPDF = incomingFile.type === "application/pdf";

    if (isHEIC || isPDF) {
      // setSnackbar({
      //   open: true,
      //   message: isHEIC
      //     ? "ℹ️ HEIC image detected. Preview disabled."
      //     : "ℹ️ PDF detected. Preview disabled.",
      //   severity: "info",
      // });
      setPreviewUrl(null);
    } else {
      const reader = new FileReader();
      reader.onload = () => setPreviewUrl(reader.result);
      reader.onerror = () => {
        setSnackbar({
          open: true,
          message: "❌ Could not preview the image.",
          severity: "error",
        });
        setPreviewUrl(null);
      };
      reader.readAsDataURL(incomingFile);
    }
  };

  const handleFileChange = (e) => processFile(e.target.files[0]);

  const isPro = plan === "pro" || plan === "proplus";
  if (!isPro && isTrialExpired) {
    return (
      <UpgradePrompt
        source="dashboard-page"
        title="🚫 Uploading receipts is now disabled."
        subtitle="Your 14-day trial has ended. Upgrade to Pro to continue creating and sending receipts."
      />
    );
  }

  return (
    <Paper elevation={3} sx={{ p: isMobile ? 2 : 4, mb: 4, borderRadius: 3 }}>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        📤 Upload Receipt
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Upload an image or PDF of a receipt and assign it to a specific month
        and year.
      </Typography>

      <Box
        sx={{
          mt: 2,
          mb: 3,
          p: 3,
          border: dragOver ? "2px dashed #3b82f6" : "2px dashed #cbd5e1", // Tailwind slate-300
          borderRadius: 2,
          textAlign: "center",
          cursor: "pointer",
          backgroundColor: dragOver ? "#f0f9ff" : "#f9fafb",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            backgroundColor: "#f0f9ff",
            borderColor: "#3b82f6",
          },
        }}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          processFile(e.dataTransfer.files[0]);
          setDragOver(false);
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf"
          onChange={handleFileChange}
          hidden
        />
        <CloudUploadIcon
          sx={{ fontSize: 48, color: dragOver ? "#3b82f6" : "#94a3b8" }}
        />
        <Typography variant="body2" mt={1} color="text.secondary">
          {file ? file.name : "Click or drag a file to upload"}
        </Typography>
      </Box>

      {previewUrl && (
        <Box sx={{ mb: 3, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary" mb={1}>
            Preview:
          </Typography>
          <img
            src={previewUrl}
            alt="Preview"
            style={{ maxWidth: "100%", maxHeight: 240, borderRadius: 8 }}
          />
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 3,
          flexWrap: "wrap",
          alignItems: "flex-start",
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Receipt Date"
            open={datePickerOpen}
            onOpen={() => setDatePickerOpen(true)}
            onClose={() => setDatePickerOpen(false)}
            value={date ? new Date(date) : null}
            onChange={(newDate) => {
              setDatePickerOpen(false);
              if (newDate instanceof Date && !isNaN(newDate)) {
                setDate(newDate.toISOString().slice(0, 10));
              }
            }}
            maxDate={new Date()}
            format="dd/MM/yyyy"
            slotProps={{
              textField: {
                error: isFutureDate,
                helperText: isFutureDate ? "Cannot select future date." : "",
                onClick: () => setDatePickerOpen(true),
                sx: { minWidth: 160, cursor: "pointer" },
                InputLabelProps: { shrink: true },
              },
            }}
          />
        </LocalizationProvider>
        {/* <NominalCodeDropdown
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        /> */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="category-select-label">Category</InputLabel>
          <Select
            labelId="category-select-label"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            label="Category"
          >
            <MenuItem value="">None</MenuItem>
            {CATEGORY_SELECT_OPTIONS.map((cat) => (
              <MenuItem key={cat.value} value={cat.value}>
                {cat.label}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>Select the expense category</FormHelperText>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="project-select-label">
            Assign to Project (optional)
          </InputLabel>
          <Select
            labelId="project-select-label"
            label="Assign to Project (optional)"
            value={selectedProjectId}
            onChange={(e) => setSelectedProjectId(e.target.value)}
          >
            <MenuItem value="">None</MenuItem>
            {projects.map((proj) => (
              <MenuItem key={proj.id} value={proj.id}>
                {proj.name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            Choose a project to link this receipt. Leave blank for no project.
          </FormHelperText>
        </FormControl>
        <TextField
          label="Notes (optional)"
          multiline
          minRows={2}
          fullWidth
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={{ mt: 2 }}
        />
      </Box>

      <Fade in={showProgress}>
        <Box sx={{ mt: 1, mb: 2 }}>
          <Typography variant="body2" color="text.secondary" mb={1}>
            {status}
            {processing && (
              <Typography
                component="span"
                variant="body2"
                sx={{ ml: 1, fontStyle: "italic", color: "#64748b" }}
              >
                (OCR may take ~3 seconds)
              </Typography>
            )}
          </Typography>

          <Box
            sx={{
              height: 8,
              width: "100%",
              backgroundColor: "#e5e7eb",
              borderRadius: 4,
              overflow: "hidden",
              position: "relative",
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: `${uploadProgress}%`,
                backgroundColor: uploadProgress === 100 ? "#10b981" : "#3b82f6",
                transition: "width 0.3s ease, background-color 0.4s ease",
              }}
            />
          </Box>

          {showSuccessIcon && (
            <Box sx={{ textAlign: "center", mt: 1 }}>
              <CheckCircleIcon
                color="success"
                sx={{ fontSize: 32, animation: "pop 0.4s ease" }}
              />
            </Box>
          )}

          {uploading && !processing && (
            <IconButton
              onClick={handleCancel}
              size="small"
              color="error"
              sx={{ mt: 1 }}
            >
              <CancelIcon fontSize="small" />
              <Typography variant="caption" sx={{ ml: 1 }}>
                Cancel Upload
              </Typography>
            </IconButton>
          )}
        </Box>
      </Fade>

      {needsRetry && (
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Button
            variant="outlined"
            onClick={() => {
              setFile(null);
              setPreviewUrl(null);
              setNeedsRetry(false);
              if (fileInputRef.current) fileInputRef.current.value = "";
            }}
          >
            🔁 Try Again with Clearer Photo
          </Button>
        </Box>
      )}

      {needsRetry && !manualEntryMode && (
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Button variant="outlined" onClick={() => setManualEntryMode(true)}>
            📝 Enter Values Manually
          </Button>
        </Box>
      )}

      {manualEntryMode && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" gutterBottom>
            Enter totals manually if OCR failed:
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              maxWidth: 300,
            }}
          >
            <TextField
              label="Total (incl. VAT)"
              type="number"
              value={manualTotal}
              onChange={(e) => setManualTotal(e.target.value)}
              required
            />
            <TextField
              label="Subtotal (ex. VAT)"
              type="number"
              value={manualSubtotal}
              onChange={(e) => setManualSubtotal(e.target.value)}
            />
            <TextField
              label="VAT"
              type="number"
              value={manualVAT}
              onChange={(e) => setManualVAT(e.target.value)}
            />
          </Box>
        </Box>
      )}
      <Button
        variant="contained"
        onClick={handleUpload}
        disabled={
          uploading ||
          isFutureDate ||
          !file ||
          (needsRetry && !manualEntryMode) || // 🔒 only block if retry + no manual input
          (manualEntryMode && !manualTotal) // 🔒 manual mode needs total
        }
        startIcon={
          uploading ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            <CloudUploadIcon />
          )
        }
      >
        {uploading
          ? processing
            ? "Finalizing..."
            : "Uploading..."
          : "Upload Receipt"}
      </Button>

      <ReviewBeforeSaveModal
        open={reviewDialogOpen}
        data={{
          ...reviewData,
          category: reviewData?.category || selectedCategory || "",
        }}
        onClose={() => {
          setReviewDialogOpen(false);
          resetUploadState();
        }}
        onSave={handleReviewSave}
        setData={setReviewData}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

export default ReceiptUploader;
